import { autoUpdate, computePosition, flip, limitShift, offset, shift } from '@floating-ui/dom'
import { PropertyValues, css, html, noChange, nothing, svg } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import type { ElementPart } from 'lit/directive.js'
import { property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { Ref, createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { Observable, Subscription, from, switchMap } from 'rxjs'
import { Constructor, SchmancyElement } from '@schmancy/mixins/index'
import { layoutViewportPlatform } from '@schmancy/overlay/overlay.soft-keyboard'
import '@schmancy/primitives/surface'
import '@schmancy/primitives/typography'
import { buildCurve, easeOutCubic } from './curves'
import { FittedYDomain, fitYDomain } from './domain'
import type { ChartCurve, ChartPadding, ChartPoint, ChartXDomain, ChartYDomain } from './types'

/** Internal design-space width for coordinate math; `preserveAspectRatio="none"`
 *  stretches it to whatever real width the host renders at, so this need not
 *  match any real pixel dimension. Height instead tracks `this.height` 1:1 so
 *  point y-coordinates equal real container pixels with no rescaling. Exported
 *  for subclasses that draw their own marks spanning the full plot width. */
export const CHART_W = 640
const STROKE_WIDTH = 2.5
const GRADIENT_OPACITY: readonly [number, number] = [0.22, 0]
export const POINT_RADIUS = 4
const ANIMATION_DURATION = 800

/** Local alias so the `Record<..., string>` maps below don't need a
 *  value-position import of the type-only `ChartColorToken`. */
type ChartColorTokenLocal = 'primary' | 'warning' | 'error' | 'success' | 'secondary'

/** Closed semantic token set → theme class lookups, shared by every chart
 *  shape's own marks (peaks, thresholds, markers, callouts). */
export const FILL: Record<ChartColorTokenLocal, string> = {
	primary: 'fill-primary-default',
	secondary: 'fill-secondary-default',
	warning: 'fill-warning-default',
	error: 'fill-error-default',
	success: 'fill-success-default',
}
export const STROKE: Record<ChartColorTokenLocal, string> = {
	primary: 'stroke-primary-default',
	secondary: 'stroke-secondary-default',
	warning: 'stroke-warning-default',
	error: 'stroke-error-default',
	success: 'stroke-success-default',
}
export const TEXT: Record<ChartColorTokenLocal, string> = {
	primary: 'text-primary-default',
	secondary: 'text-secondary-default',
	warning: 'text-warning-default',
	error: 'text-error-default',
	success: 'text-success-default',
}
export const STROKE_OUTLINE = 'stroke-outline-variant'

let instanceCounter = 0

/** A `ChartPoint` plus its computed plot position. Not generic over the
 *  consumer's own payload type — see the note on `ChartMixin` below for why. */
export interface ChartLayoutPoint {
	index: number
	x: number
	y: number
	point: ChartPoint
	isFirst: boolean
	isLast: boolean
}

/** One unbroken stretch of the line: the points from one that starts a run
 *  (the first, or one marked `breakBefore`) up to the next that does. */
export interface ChartRun {
	points: ChartLayoutPoint[]
	linePoints: string
	areaPoints: string
}

export interface ChartLayout {
	points: ChartLayoutPoint[]
	padding: Required<ChartPadding>
	chartWidth: number
	chartHeight: number
	domain: FittedYDomain
	numericX?: boolean
	/** Extended closing-balance hold, without an additional observed datum. */
	tailX?: number
	/** One curve per run of the line, so no segment crosses a break. A series
	 *  no point breaks is one run, drawn exactly as a single curve always was. */
	runs: ChartRun[]
	/** Every run's `linePoints`, end to end: with no break, the one curve the
	 *  chart draws. One polyline cannot leave a gap, so across a break this
	 *  bridges what `runs` keeps apart — draw `runs` to honour a break. */
	linePoints: string
	/** Every run's `areaPoints`, end to end. Consecutive runs meet along the
	 *  baseline, so as one polygon this fills exactly what `runs` fill. */
	areaPoints: string
	/** Smallest adjacent real-pixel gap; simultaneous coordinates have zero gap. */
	spacing: number
}

function computeY(value: number, domain: FittedYDomain, padding: Required<ChartPadding>, chartHeight: number): number {
	const span = domain.max - domain.min || 1
	return padding.top + chartHeight - ((value - domain.min) / span) * chartHeight
}

/** A chart shape's own hover-tooltip state — which point is nearest the
 *  pointer, and whether it's within the hit-test threshold right now. */
export interface ChartHoveredState {
	visible: boolean
	index: number
}

/** Pointer-distance hit-test shared by every chart shape's own `tooltip` prop
 *  (`<schmancy-area-chart>`, `<schmancy-step-chart>`). Hit-testing happens in
 *  normalized [0..1] space — a point's x lives in the CHART_W design space,
 *  not real pixels, since preserveAspectRatio="none" stretches the viewBox
 *  non-uniformly to the element's own box. On a miss, `previous`'s index
 *  carries forward (only `visible` flips false) so the card's opacity
 *  fade-out still shows the last-hovered point's data mid-transition, rather
 *  than jumping to whatever the pointer is nearest at that instant. `rect` is
 *  the caller's own `plotRef` measurement — kept out of this function so a
 *  zero-width measurement (not yet laid out) is the caller's call on whether
 *  to update state at all. */
export function nearestChartPoint(
	layout: ChartLayout,
	rect: DOMRect,
	e: PointerEvent,
	previous: ChartHoveredState,
): ChartHoveredState {
	const xFrac = (e.clientX - rect.left) / rect.width
	const thresholdFrac = 30 / rect.width

	let closestIndex = -1
	let minDistance = Infinity
	let minVerticalDistance = Infinity
	for (const p of layout.points) {
		const distance = p.isLast && layout.tailX !== undefined && xFrac >= p.x / CHART_W && xFrac <= layout.tailX / CHART_W
			? 0
			: Math.abs(p.x / CHART_W - xFrac)
		const verticalDistance = Math.abs(p.y - (e.clientY - rect.top) *
			(layout.padding.top + layout.chartHeight + layout.padding.bottom) / rect.height)
		if (distance < minDistance || (distance === minDistance && verticalDistance < minVerticalDistance)) {
			minDistance = distance
			minVerticalDistance = verticalDistance
			closestIndex = p.index
		}
	}

	return closestIndex !== -1 && minDistance < thresholdFrac
		? { visible: true, index: closestIndex }
		: { ...previous, visible: false }
}

/** Holds a hover card above the plot point it describes, inside whichever is
 *  tighter: the card's nearest clipping ancestor (shadow boundaries crossed)
 *  or the viewport. Floating UI's flip/shift over `clippingAncestors` is the
 *  collision handling the overlay layer already runs; `positionFloatingUI`
 *  itself is not reused because it lifts the surface to `position: fixed` and
 *  clamps its max size, where this card stays an `absolute` child of the plot.
 *  `limitShift` stops the shift at the point, so the card never slides off the
 *  point it names. Tracks only while shown; a hidden card fades out in place. */
class ChartPointAnchor extends AsyncDirective {
	private previous = ''
	private tracking?: Subscription

	render(_xFraction: number, _yFraction: number, _visible: boolean) {
		return noChange
	}

	override update(part: ElementPart, [xFraction, yFraction, visible]: [number, number, boolean]) {
		const key = `${xFraction}:${yFraction}:${visible}`
		if (key === this.previous) return noChange
		this.previous = key
		this.tracking?.unsubscribe()
		this.tracking = undefined

		const card = part.element
		if (!visible || !(card instanceof HTMLElement)) return noChange
		const plot = card.offsetParent
		if (!plot) return noChange

		// Both axes as fractions of the plot's painted box, so an ancestor
		// transform (a scale() entrance) moves the point with the plot.
		const point = {
			contextElement: plot,
			getBoundingClientRect: () => {
				const box = plot.getBoundingClientRect()
				return new DOMRect(box.left + xFraction * box.width, box.top + yFraction * box.height, 0, 0)
			},
		}
		this.tracking = new Observable<void>(subscriber => autoUpdate(point, card, () => subscriber.next()))
			.pipe(
				switchMap(() =>
					from(
						computePosition(point, card, {
							strategy: 'absolute',
							placement: 'top',
							middleware: [offset(12), flip({ padding: 8 }), shift({ padding: 8, limiter: limitShift() })],
							platform: layoutViewportPlatform,
						}),
					),
				),
			)
			.subscribe(({ x, y }) => {
				card.style.left = `${Math.round(x)}px`
				card.style.top = `${Math.round(y)}px`
			})
		return noChange
	}

	override disconnected() {
		this.tracking?.unsubscribe()
		this.tracking = undefined
		this.previous = ''
	}
}

const chartPointAnchor = directive(ChartPointAnchor)

/** Shared hover-tooltip card body — a plain export, not a widened
 *  `IChartMixin` member; see the doc comment on `IChartMixin` below for why
 *  adding an interface member for one card is pure duplication cost with no
 *  type-safety payoff. `layout`/`hovered` resolve which point the card
 *  describes (falling back to the last point so a not-yet-hovered card still
 *  has something to fade in from); `formatValue` is the one instance value
 *  the caller must supply, since it closes over that chart's own
 *  `valueFormatter`. Render-gate this behind the caller's own opt-in prop
 *  (`this.tooltip`) — this function always renders the card, opacity-toggled
 *  only by `hovered.visible`. */
export function renderChartTooltipCard(
	layout: ChartLayout,
	hovered: ChartHoveredState,
	formatValue: (v: number, p: ChartPoint) => string,
): unknown {
	const point = layout.points[hovered.index] ?? layout.points[layout.points.length - 1]
	return html`
		<div
			class=${classMap({
				absolute: true,
				'pointer-events-none': true,
				'transition-opacity': true,
				'duration-150': true,
				'opacity-100': hovered.visible,
				'opacity-0': !hovered.visible,
			})}
			${chartPointAnchor(
				point.x / CHART_W,
				point.y / (layout.padding.top + layout.chartHeight + layout.padding.bottom),
				hovered.visible,
			)}
		>
			<schmancy-surface type="containerLow" elevated class="rounded-xs px-3 py-2 min-w-max">
				<schmancy-typography type="label" token="sm" class="text-surface-on-variant">
					${point.point.label ?? ''}
				</schmancy-typography>
				<schmancy-typography type="title" token="md" class="text-surface-on font-semibold">
					${formatValue(point.point.value, point.point)}
				</schmancy-typography>
				${when(point.point.meta, () =>
					repeat(
						Object.entries(point.point.meta ?? {}),
						([key]) => key,
						([key, value]) => html`
							<schmancy-typography type="body" token="sm" class="text-surface-on-variant">
								${key}: ${String(value)}
							</schmancy-typography>
						`,
					),
				)}
			</schmancy-surface>
		</div>
	`
}

/**
 * The engine surface a concrete chart shape (`<schmancy-area-chart>`,
 * `<schmancy-step-chart>`, `<schmancy-line-chart>`) sees when it extends
 * `ChartMixin(SchmancyElement)` — the mixin's return value is cast to this
 * (mirroring `FormFieldMixin`/`IFormFieldMixin`) because `tsconfig`'s
 * `emitDeclarationOnly` can't emit a `.d.ts` for an anonymous class carrying
 * `private`/`protected` members. These members are engine-internal by
 * convention, not by the TypeScript `protected` keyword — a concrete chart
 * overrides `render*`/`handlePointer*` to draw only its own marks, and reads
 * `layout`/`plotRef` for its own hit-testing (hover tooltip, threshold rules).
 */
export interface IChartMixin {
	data: ChartPoint[]
	height: number
	padding?: ChartPadding
	yDomain?: ChartYDomain
	xDomain?: ChartXDomain
	fillBaseline?: number
	valueFormatter?: (v: number, p: ChartPoint) => string

	/** Fixed per concrete chart shape (smooth/step/linear) — never a
	 *  runtime-settable prop, so subclasses just override the field value. */
	curve: ChartCurve
	/** Entrance-animation toggle. Plain engine field by default — no reveal
	 *  animation — since only `<schmancy-area-chart>` exposes this as a
	 *  reactive `@property` for consumers to opt into. */
	animated: boolean
	/** Concrete charts may turn their painted marks into interaction targets.
	 *  The engine uses this only to let the mark layer receive pointer input. */
	interactive: boolean
	/** Optional click path for a continuous plot surface. Concrete charts may
	 *  resolve a click between point marks to their nearest datum. */
	handlePlotClick: (e: MouseEvent) => void

	readonly layout: ChartLayout | null
	readonly plotRef: Ref<HTMLDivElement>
	/** Real rendered plot px width, measured via `ResizeObserver` on `plotRef`.
	 *  `0` means "not yet measured" — a consumer spending it on label thinning
	 *  reads that as "render everything", so first paint is never thinner than
	 *  before this measurement existed. */
	readonly plotWidth: number

	/** Padding defaults before consumer `.padding` overrides. Base is a flat
	 *  20px frame; a subclass that always renders static labels (value
	 *  callouts, x-axis captions) bumps the relevant edge so its own marks
	 *  have room, without the mixin needing to know which labels exist. */
	defaultPadding(): Required<ChartPadding>
	formatValue(v: number, p: ChartPoint): string
	/** Converts a raw data value into its y pixel position using the current
	 *  layout's fitted domain — for a subclass's own marks (threshold rules,
	 *  reference bands) that aren't one of the plotted `data` points. */
	yFor(value: number): number
	/** `p.x` lives in the CHART_W design space, not real pixels — the
	 *  viewBox's `preserveAspectRatio="none"` stretch is non-uniform. */
	xPercent(p: ChartLayoutPoint): number
	/** Right-anchors the last point's overlay so it never clips off the
	 *  plot's trailing edge; every other point anchors from the left. */
	horizontalAnchorStyle(p: ChartLayoutPoint): Record<string, string>
	horizontalAlignment(p: ChartLayoutPoint): 'left' | 'center' | 'right'
	/** Which points of a one-per-point label row survive without colliding —
	 *  see the implementation's comment for the walk. `boxWidth` is the row's
	 *  measured widest string (every such row is `tabular-nums`, so one hidden
	 *  probe per row is enough); `forcedIndex` is a point whose label must
	 *  survive regardless, typically the selected one. */
	keptLabelIndices(layout: ChartLayout, boxWidth: number, forcedIndex: number): Set<number>

	/** Seam for marks drawn BEHIND the area/line, outside its clip-path —
	 *  gridlines/threshold bands render immediately, unaffected by the
	 *  entrance-reveal animation. Must return an `svg`-tagged template (or
	 *  `nothing`) since its root sits directly under `<svg>`. */
	renderBackgroundMarks(layout: ChartLayout): unknown
	/** Seam for marks that must NOT take the main viewBox's stretch — dots,
	 *  peak halos, markers. They render in a second, viewBox-less `<svg>` whose
	 *  user units ARE CSS pixels, so a `<circle>` stays round at every plot
	 *  width instead of squashing to `plotWidth / CHART_W`. Give `cx` as a
	 *  percentage (`xPercent(p)`) and `cy` as `p.y` — both are already
	 *  scale-free, so nothing here needs a width measurement. Reveals in
	 *  lockstep with the area/line while the entrance animation runs. Must
	 *  return an `svg`-tagged template (or `nothing`). */
	renderUnstretchedMarks(layout: ChartLayout): unknown
	/** Whether the shape paints its own mark on `p`. The engine marks a run one
	 *  point long itself only where this is false, so no point carries a second
	 *  mark hidden under the shape's. */
	marksPoint(layout: ChartLayout, p: ChartLayoutPoint): boolean
	/** Seam for HTML overlays (buttons, callouts, tooltip cards) positioned
	 *  absolutely over the plot, alongside the SVG. */
	renderOverlays(layout: ChartLayout): unknown

	/** No-op by default; `<schmancy-area-chart>` and `<schmancy-step-chart>`
	 *  override for their own opt-in `tooltip` prop. Instance-field arrow
	 *  functions (not methods) so `this` stays correct and the reference is
	 *  stable across renders for the `@pointermove`/`@pointerleave` bindings. */
	handlePointerMove: (e: PointerEvent) => void
	handlePointerLeave: () => void
}

/**
 * Engine shared by every schmancy chart shape — layout math (domain fit,
 * curve sampling), the two-SVG shell (the stretched plot's viewBox, gradient
 * and clip-path reveal, then the viewBox-less marks layer over it), the
 * area+line paint, overlay-label placement helpers, and the entrance
 * animation. A concrete chart extends `ChartMixin(SchmancyElement)`, sets its
 * own `curve`, and overrides the `IChartMixin` seams to draw only its own
 * marks and overlays — see `IChartMixin` for the full engine surface.
 *
 * `data`/`ChartPoint` are not threaded through a class-level generic —
 * TypeScript's mixin-constructor check (TS2545) rejects a class that both
 * `extends` a type-parameter-typed superclass AND declares its own type
 * parameter. `ChartPoint<T>`'s own default (`T = unknown`) covers the engine;
 * a concrete chart is free to declare its own class generic for a callback
 * prop it owns (`colorFor`, `ariaLabelFor`) without hitting the same
 * restriction, since by then the heritage clause is a concrete class.
 */
export function ChartMixin<TBase extends Constructor<SchmancyElement>>(superClass: TBase) {
	class ChartMixinClass extends superClass {
		static styles = [
			css`
				:host {
					display: block;
				}
			`,
		]

		@property({ type: Array }) data: ChartPoint[] = []
		@property({ type: Number }) height = 200
		@property({ type: Object }) padding?: ChartPadding
		@property({ type: Object }) yDomain?: ChartYDomain
		@property({ type: Object }) xDomain?: ChartXDomain
		@property({ type: Number }) fillBaseline?: number
		@property({ attribute: false }) valueFormatter?: (v: number, p: ChartPoint) => string

		curve: ChartCurve = 'linear'
		animated = false
		interactive = false
		handlePlotClick = (_e: MouseEvent): void => {}

		@state() private animationProgress = 0
		// Plain field, not @state — never read in render(); animationProgress
		// already drives the re-render this bookkeeping needs.
		private visible = false

		private readonly instanceId = ++instanceCounter
		private containerRef: Ref<HTMLDivElement> = createRef()
		plotRef: Ref<HTMLDivElement> = createRef()
		private animationFrame: number | null = null
		private observer: IntersectionObserver | null = null
		private plotObserver: ResizeObserver | null = null
		layout: ChartLayout | null = null
		private fillFade = { top: 0, bottom: 0 }

		// Not `private` despite `@state()`: `IChartMixin` exposes this field, and
		// a TS `private` member fails the mixin's own cast to that interface
		// (same convention as `layout`/`plotRef` above — see the doc comment on
		// `IChartMixin`).
		@state() plotWidth = 0

		connectedCallback() {
			super.connectedCallback()
			this.observer = new IntersectionObserver(
				entries => {
					const entry = entries[0]
					if (entry.isIntersecting && !this.visible) {
						this.visible = true
						if (this.animated) this.startAnimation()
						else this.animationProgress = 1
					} else if (!entry.isIntersecting) {
						this.visible = false
						if (this.animationFrame !== null) {
							cancelAnimationFrame(this.animationFrame)
							this.animationFrame = null
						}
					}
				},
				{ threshold: 0.1 },
			)

			// Guarded because schmancy ships as a library whose modules can be
			// imported without a DOM present, where `ResizeObserver` may not
			// exist — not a test-lane guard, since both test lanes run real
			// headless Chromium and already construct one elsewhere unguarded.
			if (typeof ResizeObserver !== 'undefined') {
				this.plotObserver = new ResizeObserver(entries => {
					const entry = entries[0]
					if (entry) this.plotWidth = entry.contentRect.width
				})
			}
		}

		disconnectedCallback() {
			super.disconnectedCallback()
			if (this.animationFrame !== null) cancelAnimationFrame(this.animationFrame)
			this.observer?.disconnect()
			this.observer = null
			this.plotObserver?.disconnect()
			this.plotObserver = null
		}

		defaultPadding(): Required<ChartPadding> {
			return { top: 20, right: 20, bottom: 20, left: 20 }
		}

		/** Recomputes chart geometry (domain fit, curve sampling) before every
		 *  render — SVG is declarative, so once this and `animationProgress` are
		 *  current, `render()` alone reflects any data/config change with no
		 *  imperative draw call. */
		protected willUpdate(changed: PropertyValues) {
			super.willUpdate(changed)

			// From here and never `updated()`: `animationProgress` is reactive, so
			// the reset written after render schedules a trailing update (Lit's
			// `change-in-update` warning); written here it folds into the update
			// in flight. `startAnimation`'s own writes ride rAF, outside any cycle.
			if (changed.has('data') && this.visible) {
				if (this.animated) {
					this.animationProgress = 0
					this.startAnimation()
				} else {
					this.animationProgress = 1
				}
			}

			if (!this.data || this.data.length < 2) {
				this.layout = null
				return
			}

			const defaults = this.defaultPadding()
			const padding: Required<ChartPadding> = {
				top: this.padding?.top ?? defaults.top,
				right: this.padding?.right ?? defaults.right,
				bottom: this.padding?.bottom ?? defaults.bottom,
				left: this.padding?.left ?? defaults.left,
			}
			const chartWidth = CHART_W - padding.left - padding.right
			const chartHeight = this.height - padding.top - padding.bottom
			const domain = fitYDomain(
				this.data.map(p => p.value),
				this.yDomain ?? {},
			)
			const baseY = this.fillBaseline === undefined
				? padding.top + chartHeight
				: computeY(this.fillBaseline, domain, padding, chartHeight)

			const n = this.data.length
			const coordinates = this.data.map(p => p.x)
			const numericX = coordinates.every((x): x is number => typeof x === 'number' && Number.isFinite(x))
			const xMin = numericX ? this.xDomain?.min ?? Math.min(...coordinates) : 0
			const xMax = numericX ? this.xDomain?.max ?? Math.max(...coordinates) : n - 1
			const xSpan = xMax - xMin || 1
			const points: ChartLayoutPoint[] = this.data.map((point, index) => ({
				index,
				x: padding.left + (((numericX ? coordinates[index] : index) - xMin) / xSpan) * chartWidth,
				y: computeY(point.value, domain, padding, chartHeight),
				point,
				isFirst: index === 0,
				isLast: index === n - 1,
			}))
			const tailX = numericX && this.curve === 'step' && this.xDomain?.max !== undefined && xMax > coordinates[n - 1]
				? padding.left + chartWidth
				: undefined

			const curves = points
				.reduce<ChartLayoutPoint[][]>((acc, p) => {
					if (p.isFirst || p.point.breakBefore) acc.push([p])
					else acc[acc.length - 1].push(p)
					return acc
				}, [])
				.map(run => ({
					run,
					curvePoints: buildCurve(
						this.curve,
						tailX !== undefined && run[run.length - 1].isLast
							? [...run.map(p => ({ x: p.x, y: p.y })), { x: tailX, y: run[run.length - 1].y }]
							: run.map(p => ({ x: p.x, y: p.y })),
					),
				}))
			const runs = curves.map(({ run, curvePoints }): ChartRun => {
				const linePoints = curvePoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
				return {
					points: run,
					linePoints,
					areaPoints: curvePoints.length
						? `${curvePoints[0].x.toFixed(1)},${baseY} ${linePoints} ${curvePoints[curvePoints.length - 1].x.toFixed(1)},${baseY}`
						: '',
				}
			})
			// One fade under every run, so a low stretch never opens as strong as a
			// high one. Each fill spans its curve down to baseY, so this is the union
			// of their boxes, read off the same rounded vertices they draw: for a
			// single run, exactly the box objectBoundingBox units faded it across.
			const fadeYs = [baseY, ...curves.flatMap(({ curvePoints }) => curvePoints.map(p => Number(p.y.toFixed(1))))]
			this.fillFade = { top: Math.min(...fadeYs), bottom: Math.max(...fadeYs) }

			const spacing = this.plotWidth > 0
				? Math.min(...points.slice(1).map((p, i) => Math.abs(p.x - points[i].x))) * this.plotWidth / CHART_W
				: 0

			this.layout = {
				points,
				padding,
				chartWidth,
				chartHeight,
				domain,
				numericX,
				tailX,
				runs,
				linePoints: runs.map(run => run.linePoints).join(' '),
				areaPoints: runs.map(run => run.areaPoints).join(' '),
				spacing,
			}
		}

		protected updated(changed: PropertyValues) {
			super.updated(changed)

			if (this.containerRef.value && this.observer) {
				this.observer.observe(this.containerRef.value)
			}

			if (this.plotRef.value && this.plotObserver) {
				this.plotObserver.observe(this.plotRef.value)
			}
		}

		private startAnimation(): void {
			const startTime = performance.now()
			const duration = ANIMATION_DURATION

			const animateFrame = (currentTime: number) => {
				const elapsed = currentTime - startTime
				// Clamp low as well as high: a rAF callback's timestamp is the frame's
				// START time, which can predate the `performance.now()` startTime, making
				// the first frame's elapsed slightly negative → easeOutCubic(t<0) < 0 →
				// a negative clip-rect width the SVG rejects. Floor at 0 to prevent it.
				const progress = Math.min(Math.max(elapsed / duration, 0), 1)

				this.animationProgress = easeOutCubic(progress)

				if (progress < 1 && this.visible) {
					this.animationFrame = requestAnimationFrame(animateFrame)
				}
			}

			this.animationFrame = requestAnimationFrame(animateFrame)
		}

		formatValue(v: number, p: ChartPoint): string {
			return this.valueFormatter?.(v, p) ?? String(v)
		}

		yFor(value: number): number {
			const layout = this.layout
			if (!layout) return 0
			return computeY(value, layout.domain, layout.padding, layout.chartHeight)
		}

		xPercent(p: ChartLayoutPoint): number {
			return (p.x / CHART_W) * 100
		}

		horizontalAnchorStyle(p: ChartLayoutPoint): Record<string, string> {
			return this.horizontalAlignment(p) === 'right'
				? { right: `${100 - this.xPercent(p)}%` }
				: { left: `${this.xPercent(p)}%` }
		}

		horizontalAlignment(p: ChartLayoutPoint): 'left' | 'center' | 'right' {
			if (this.layout?.numericX) {
				const fraction = (p.x - this.layout.padding.left) / this.layout.chartWidth
				if (fraction < 0.25) return 'left'
				if (fraction > 0.75) return 'right'
				return 'center'
			}
			if (p.isFirst) return 'left'
			return p.isLast ? 'right' : 'center'
		}

		/** Greedy right-edge gating for one label row: walk points left→right,
		 *  keeping a label only when its own anchored box (real px, from
		 *  `horizontalAnchorStyle`'s first/last/interior anchoring) clears the
		 *  last kept box's right edge. `forcedIndex` wins collisions; the last
		 *  point is kept when it clears that label, evicting other neighbours to make
		 *  room. An unmeasured plot short-circuits to
		 *  "keep everything", so first paint is never thinner than an ungated row.
		 *
		 *  Lives on the engine rather than in one chart shape: `ChartPoint.label`
		 *  documents that the CHART decides which points show it, and a second
		 *  unmeasured heuristic in a sibling shape would make that promise mean
		 *  two different things. */
		keptLabelIndices(layout: ChartLayout, boxWidth: number, forcedIndex: number): Set<number> {
			if (this.plotWidth === 0) return new Set(layout.points.map(p => p.index))

			const boxes = layout.points.map(p => {
				const realX = (p.x / CHART_W) * this.plotWidth
				const alignment = this.horizontalAlignment(p)
				const [left, right] = alignment === 'left'
					? [realX, realX + boxWidth]
					: alignment === 'right'
						? [realX - boxWidth, realX]
						: [realX - boxWidth / 2, realX + boxWidth / 2]
				return { index: p.index, left, right, isLast: p.isLast }
			})
			const selected = boxes.find(box => box.index === forcedIndex)
			const keep = new Set<number>()
			let cursor = -Infinity
			for (const box of boxes) {
				if (selected && box.index !== selected.index && box.left < selected.right && box.right > selected.left) continue
				if (box.left >= cursor || box.index === forcedIndex || box.isLast) {
					if (box.isLast) {
						for (const previous of boxes) {
							if (previous.index !== forcedIndex && previous.right > box.left) keep.delete(previous.index)
						}
					}
					keep.add(box.index)
					cursor = box.right
				}
			}
			return keep
		}

		renderBackgroundMarks(_layout: ChartLayout): unknown {
			return nothing
		}

		renderUnstretchedMarks(_layout: ChartLayout): unknown {
			return nothing
		}

		marksPoint(_layout: ChartLayout, _p: ChartLayoutPoint): boolean {
			return false
		}

		renderOverlays(_layout: ChartLayout): unknown {
			return nothing
		}

		handlePointerMove = (_e: PointerEvent): void => {}
		handlePointerLeave = (): void => {}

		render() {
			const layout = this.layout
			if (!layout) return html``

			const gradientId = `schmancy-chart-gradient-${this.instanceId}`
			const clipId = `schmancy-chart-clip-${this.instanceId}`
			const marksClipId = `schmancy-chart-marks-clip-${this.instanceId}`
			// `padding.left + chartWidth` is the last point's own x by construction,
			// so the reveal's right edge bisects the trailing mark, and widening it
			// to 100% only moves that edge onto the svg box — which a mark centred
			// a fraction of a percent inside still reaches. At rest the reveal is
			// therefore released, not widened: the plot's rect snaps to the full
			// design space, freeing the line's end cap, and the marks layer drops
			// its clip altogether.
			const revealed = this.animationProgress >= 1
			const revealWidth = revealed ? CHART_W : layout.padding.left + layout.chartWidth * this.animationProgress
			// A run one point long has no segment to stroke and no area to fill, so
			// it is marked here wherever the shape leaves its point unmarked. The
			// line's ink at twice its stroke reads as the line one reading long; at
			// POINT_RADIUS it would read as a second selection marker.
			const marks = svg`${repeat(
				layout.runs.filter(run => run.points.length === 1 && !this.marksPoint(layout, run.points[0])),
				run => run.points[0].index,
				run =>
					svg`<circle cx="${this.xPercent(run.points[0])}%" cy=${run.points[0].y} r=${STROKE_WIDTH} fill="currentColor"></circle>`,
			)}${this.renderUnstretchedMarks(layout)}`

			return html`
				<div ${ref(this.containerRef)} class="relative">
					<div
						${ref(this.plotRef)}
						class="relative w-full @container touch-pan-y text-primary-default"
						style=${this.styleMap({ height: `${this.height}px` })}
						@pointermove=${this.handlePointerMove}
						@pointerleave=${this.handlePointerLeave}
						@click=${this.handlePlotClick}
					>
						<svg
							viewBox="0 0 ${CHART_W} ${this.height}"
							preserveAspectRatio="none"
							class="absolute inset-0 w-full h-full"
						>
							<defs>
								<linearGradient
									id=${gradientId}
									gradientUnits="userSpaceOnUse"
									x1="0"
									y1=${this.fillFade.top}
									x2="0"
									y2=${this.fillFade.bottom}
								>
									<stop offset="0" stop-color="currentColor" stop-opacity=${GRADIENT_OPACITY[0]}></stop>
									<stop offset="1" stop-color="currentColor" stop-opacity=${GRADIENT_OPACITY[1]}></stop>
								</linearGradient>
								<clipPath id=${clipId}>
									<rect x="0" y="0" width=${revealWidth} height=${this.height}></rect>
								</clipPath>
							</defs>

							${this.renderBackgroundMarks(layout)}

							<g clip-path="url(#${clipId})">
								${repeat(
									layout.runs,
									(_run, i) => i,
									run => svg`
										<polygon points=${run.areaPoints} fill="url(#${gradientId})"></polygon>
										<polyline
											points=${run.linePoints}
											fill="none"
											stroke="currentColor"
											stroke-width=${STROKE_WIDTH}
											stroke-linejoin="round"
											stroke-linecap="round"
											vector-effect="non-scaling-stroke"
										></polyline>
									`,
								)}
							</g>
						</svg>

						<!-- No viewBox: user units are CSS pixels here, so a round mark stays
						     round (see renderUnstretchedMarks). overflow-visible then lets the
						     trailing mark paint its outer edge, which an SVG viewport otherwise
						     clips away — the last point sits inside the right edge by a
						     percentage of the box, so on a narrow plot that gap is under one
						     radius. Ordering is the real constraint on this layer: it must stay
						     between the plot and renderOverlays, whose hit targets and hover
						     tint paint over the marks. -->
						<svg
							class=${classMap({
								'absolute inset-0 w-full h-full overflow-visible': true,
								'pointer-events-auto': this.interactive,
								'pointer-events-none': !this.interactive,
							})}
						>
							${when(
								revealed,
								() => marks,
								() => svg`
									<defs>
										<clipPath id=${marksClipId}>
											<rect x="0" y="0" width="${(revealWidth / CHART_W) * 100}%" height="100%"></rect>
										</clipPath>
									</defs>
									<g clip-path="url(#${marksClipId})">${marks}</g>
								`,
							)}
						</svg>

						${this.renderOverlays(layout)}
					</div>
				</div>
			`
		}
	}

	return ChartMixinClass as Constructor<IChartMixin> & TBase
}
